import React from "react";
import { createBrowserRouter, Navigate, redirect, RouterProvider } from "react-router-dom";
import { runInAction } from 'mobx'

import "./App.css"
import { MainPage } from "./Pages/MainPage/MainPage";
import { CheckoutPage } from "./Pages/CheckoutPage/CheckoutPage";
import { ReviewOrderSubpage } from "./Pages/CheckoutPage/Subpages/Step1ReviewOrder";
import { DeliveryDetailsSubpage } from "./Pages/CheckoutPage/Subpages/Step2DeliveryDetails";
import { PaymentSubpage } from "./Pages/CheckoutPage/Subpages/Step3Payment";
import { OrderConfirmationSubpage } from "./Pages/CheckoutPage/Subpages/Step4OrderConfirmation";
import { AllIssuesOutlet } from "./Pages/MainPage/Components/Outlets/AllIssuesOutlet";
import { TextualIssueOutlet } from "./Pages/MainPage/Components/Outlets/TextualIssueOutlet";
import { filteringController } from "./Pages/MainPage/Controllers/FilteringController";
import { AccountPage } from "./Pages/AccountPage/AccountPage";
import { UserSubpage } from "./Pages/AccountPage/Subpages/UserSubpage";
import { accountPageController } from "./Pages/AccountPage/Controllers/AccountPageController";
import { InfoPage } from "./Pages/InfoPage/InfoPage";
import { AddressSubpage } from "./Pages/AccountPage/Subpages/AddressSubpage";
import { stockiestPortalController } from "./Common/Controllers/StockiestPortalController";
import { OrdersSubpage } from "./Pages/AccountPage/Subpages/OrdersSubpage";
import { orderSubpageController } from "./Pages/AccountPage/Controllers/OrderSubpageController";
import { SsoRedirectPage } from "./Pages/AccountPage/Components/SsoRedirectPage";
import { textualIssueOutletController } from "./Pages/MainPage/Controllers/TextualIssueOutletController";
import { PasswordResetPage } from "./Pages/PasswordResetPage";
import { SettingsSubpage } from "./Pages/AccountPage/Subpages/SettingsSubpage";
import { issueController } from "./Common/Controllers/IssueController";
import { SearchOutlet } from "./Pages/MainPage/Components/Outlets/SearchOutlet";
import { IssueDetailsPage } from "./Pages/MainPage/Components/Outlets/IssueDetailsPage";
import { issueDetailsController } from "./Pages/MainPage/Controllers/IssueDetailsController";

export const router = createBrowserRouter([
  {
    element: <MainPage />,
    path: "/",
    children: [
      {
        index: true,
        element: <Navigate to="/latest" replace />
      },
      {
        element: <AllIssuesOutlet />,
        path: "latest",
        loader: () => {
          filteringController.onSearchValueChange("");
          filteringController.setTab("latest")
          filteringController.lastNonSearchPath = "/latest";
          issueDetailsController.clear();
          runInAction(() => stockiestPortalController.showIssueNavigation = true)
          if (stockiestPortalController.issues.length === 0 && !stockiestPortalController.loadingIssues)
            void issueController.pullMoreIssues()
          return "latest"
        }
      },
      {
        element: <TextualIssueOutlet />,
        path: "upcoming",
        loader: ({ request }) => {
          filteringController.onSearchValueChange("");
          filteringController.setTab("upcoming")
          filteringController.lastNonSearchPath = "/upcoming";
          issueDetailsController.clear();
          runInAction(() => stockiestPortalController.showIssueNavigation = true)

          const url = new URL(request.url);
          const issueId = url.searchParams.get("issueId");

          if (issueId) {
            void textualIssueOutletController.searchUpcomingIssues(issueId);
            return redirect("/upcoming")
          }

          if (!textualIssueOutletController.isLoadingUpcomingIssues) {
            if (filteringController.searchValue)
              void textualIssueOutletController.searchUpcomingIssues(filteringController.searchValue);
            else if ((textualIssueOutletController.upcomingWeekIssues.length === 0 || textualIssueOutletController.upcomingMonthIssues.length === 0))
              void textualIssueOutletController.getUpcomingIssues();
          }
          return "upcoming"
        }
      },
      {
        element: <AllIssuesOutlet />,
        path: "extras",
        loader: () => {
          filteringController.onSearchValueChange("");
          filteringController.setTab("extras")
          filteringController.lastNonSearchPath = "/extras";
          issueDetailsController.clear();
          runInAction(() => stockiestPortalController.showIssueNavigation = true)
          if (!stockiestPortalController.isIssuesForExtrasLoading)
            void stockiestPortalController.pullIssuesForExtras();
          return "extras"
        }
      },
      {
        path: "magazine/:magazineSlug",
        element: <IssueDetailsPage />,
        loader: async ({ params }) => {
          const name = params.magazineSlug;
          void issueDetailsController.loadCurrentIssueIdByMagazineName(name);

          filteringController.onSearchValueChange("");
          filteringController.setTab("other");
          filteringController.lastNonSearchPath = `/magazine/${name}`;
          runInAction(() => stockiestPortalController.showIssueNavigation = true);
          return "magazine";
        }
      },
      {
        path: "issue/:id",
        element: <IssueDetailsPage />,
        loader: ({ params }) => {
          filteringController.onSearchValueChange("");
          filteringController.setTab("other");
          filteringController.lastNonSearchPath = `/issue/${params.id}`;
          runInAction(() => stockiestPortalController.showIssueNavigation = true);

          const issueId = Number(params.id);

          void issueDetailsController.loadIssue(issueId);
          return "issue-details";
        }
      },
      {
        element: <SearchOutlet />,
        path: "search",
        loader: ({ request }) => {
          runInAction(() => {
            stockiestPortalController.showIssueNavigation = false;
          });

          const url = new URL(request.url);
          const query = url.searchParams.get("query");

          issueDetailsController.clear();
          filteringController.initialiseSearchFromUrl(query);

          return "search";
        }
      },
    ]
  },
  {
    path: "/checkout",
    element: <CheckoutPage />,
    children: [
      {
        index: true,
        element: <Navigate to="/checkout/step-1" replace />
      },
      {
        element: <ReviewOrderSubpage />,
        path: "step-1",
        loader: () => {
          filteringController.onSearchValueChange("");
          issueDetailsController.clear();
          runInAction(() => stockiestPortalController.showIssueNavigation = false)
          return "step-1"
        },
      },
      {
        element: <DeliveryDetailsSubpage />,
        path: "step-2",
        loader: () => {
          filteringController.onSearchValueChange("");
          issueDetailsController.clear();
          runInAction(() => stockiestPortalController.showIssueNavigation = false)
          return "step-2";
        },
      },
      {
        element: <PaymentSubpage />,
        path: "step-3",
        loader: () => {
          filteringController.onSearchValueChange("");
          issueDetailsController.clear();
          runInAction(() => stockiestPortalController.showIssueNavigation = false)
          return "step-3"
        }
      },
      {
        element: <OrderConfirmationSubpage />,
        path: "step-4/:systemTransactionId",
        loader: () => {
          filteringController.onSearchValueChange("");
          issueDetailsController.clear();
          runInAction(() => stockiestPortalController.showIssueNavigation = false)
          return "step-4"
        }
      }
    ]
  },
  {
    path: "/about",
    element: <InfoPage />,
    loader: () => {
      issueDetailsController.clear();
      runInAction(() => stockiestPortalController.showIssueNavigation = false)
      return "about"
    }
  },
  {
    path: "/account",
    element: <AccountPage />,
    children: [
      {
        index: true,
        loader: () => {
          runInAction(() => {
            filteringController.onSearchValueChange("");
            issueDetailsController.clear();
            stockiestPortalController.showIssueNavigation = false
            accountPageController.setCurrentTab("profile");
          });
          return redirect("/account/profile");
        },
      },
      {
        element: <UserSubpage />,
        path: "profile",
        loader: () => {
          runInAction(() => {
            filteringController.onSearchValueChange("");
            issueDetailsController.clear();
            stockiestPortalController.showIssueNavigation = false
            accountPageController.setCurrentTab("profile")
          })
          return "profile";
        }
      },
      {
        element: <SettingsSubpage />,
        path: "settings",
        loader: () => {
          runInAction(() => {
            filteringController.onSearchValueChange("");
            issueDetailsController.clear();
            stockiestPortalController.showIssueNavigation = false
            accountPageController.setCurrentTab("settings")
          })
          return "settings";
        }
      },
      {
        element: <AddressSubpage />,
        path: "addresses",
        loader: () => {
          runInAction(() => {
            filteringController.onSearchValueChange("");
            stockiestPortalController.showIssueNavigation = false
            accountPageController.setCurrentTab("addresses")
          })
          return "addresses";
        },
      },
      {
        element: <OrdersSubpage />,
        path: "orders",
        loader: () => {
          runInAction(() => {
            filteringController.onSearchValueChange("");
            issueDetailsController.clear();
            stockiestPortalController.showIssueNavigation = false
            accountPageController.setCurrentTab("orders")
          })
          void orderSubpageController.fetchStockistOrders();
          return "orders";
        },
      }
    ]
  },
  {
    path: "/unsubscribe",
    element: <SsoRedirectPage />
  },
  {
    path: "/password-reset",
    element: <PasswordResetPage />
  }
]);

export const App = () => (
  <div className="bg-main text-white min-h-screen flex flex-col">
    <RouterProvider router={router} />
  </div>
)